import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@onchainstudios/cryptoys-common-ui';
import styles from './ConfirmOrder.module.css';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import PaymentSteps from '../../../components/PaymentSteps';
import { useMakePurchase } from '../../../lib/cryptoys/drops/purchase';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatCurrencyUSD,
  resetReservation,
  calculateAge
} from '../../../utils';
import { genericErrorMessage } from '../../../lib/cryptoys/utils';
import TermsConditions from '../../../components/TermsConditions/TermsConditions';
import { useMakeDeposit } from '../../../lib/cryptoys/drops/deposits';
import { useGetOrderStatus } from '../../../lib/cryptoys/payments/deposits/status';
import useTimer from '../../../utils/hooks/useTimer';
import { RECAPTCHA_ERROR_CODE } from '../../../constants';
import Loading from '../../../components/Loading/Loading';

function ConfirmOrder({ user, recaptchaToken, dropData, reservationData }) {
  const { isExpired } = useTimer();
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const makePurchase = useMakePurchase();
  const makeDeposit = useMakeDeposit();
  const {
    data: orderStatusData,
    isInitialLoading: isOrderStatusInitialLoading
  } = useGetOrderStatus({
    recaptchaToken,
    depositId: searchParams.get('depositId'),
    correlationId: searchParams.get('correlationId'),
    isEnabled: !makeDeposit.isSuccess
  });
  const [totalPriceWithoutFee, setTotalPriceWithoutFee] = useState();
  const [fee, setFee] = useState();
  const numCubes = parseInt(searchParams.get('numCubes') ?? 1);
  const isCreditCardPurchase =
    searchParams.get('isCreditCardPurchase') === 'true';
  const [authResult, setAuthResult] = useState();
  const [clientToken, setClientToken] = useState();
  const paymentErrorMessage =
    'Sorry, we couldn’t verify your payment information. Please try again.';

  const resetDeposit = () => {
    setClientToken(null);
    searchParams.delete('depositId');
    setSearchParams(searchParams.toString(), { replace: true });
  };

  const handleFinalOrderStatus = (status) => {
    if (status === 'processed') {
      purchase();
    } else if (status === 'declined') {
      resetDeposit();
      if (isExpired) {
        searchParams.set(
          'timeoutErrorMessage',
          encodeURIComponent(
            'Your card declined and you ran out of time to check out, but if you hurry you might still be able to purchase a Cryptoys Cube!'
          )
        );
        navigate({
          pathname: '/time-expired',
          search: '?' + searchParams
        });
      } else {
        searchParams.set(
          'error',
          encodeURIComponent(
            'This card was declined. Please use another payment method.'
          )
        );
        navigate({
          pathname: '/',
          search: '?' + searchParams
        });
      }
    } else if (status === 'expired') {
      resetReservation(
        searchParams.get('dropId'),
        'Your session expired. Please try again.'
      );
    }
  };

  useEffect(() => {
    // Redirect to start page if no reservation ID in url
    if (!searchParams.get('reservationId')) {
      navigate(
        { pathname: '/', search: '?' + searchParams },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderStatusData) {
      if (orderStatusData.data.status === 'draft') {
        searchParams.delete('depositId');
        setSearchParams(searchParams.toString(), { replace: true });
      } else {
        handleFinalOrderStatus(orderStatusData.data.status);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStatusData]);

  // Set state with drop and search param data
  useEffect(() => {
    if (dropData && reservationData) {
      const cardFee = 0.05;
      const price = dropData.data.price * numCubes;
      setTotalPriceWithoutFee(price);
      setFee(price * cardFee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropData, reservationData, numCubes]);

  useEffect(() => {
    document.title = 'Cryptoys - Review Your Order Summary';
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Post message event handler
  const handleMessage = useCallback((event) => {
    if (event.source !== iframeRef?.current?.contentWindow) {
      return;
    } else {
      setAuthResult(JSON.parse(event.data));
    }
  }, []);

  // Listens for message events if widget is showing
  useEffect(
    () => {
      if (reservationData && clientToken) {
        window.addEventListener('message', handleMessage, false);
        if (authResult) {
          window.removeEventListener('message', handleMessage);
          handleFinalOrderStatus(authResult.status);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reservationData, authResult, clientToken]
  );

  useEffect(() => {
    function handleUnload(e) {
      if (clientToken || authResult) {
        e.preventDefault();
        return (e.returnValue =
          'Your deposit is in progress. Are you sure you would like to leave the page?');
      }
    }
    window.addEventListener('beforeunload', handleUnload);

    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [clientToken, authResult]);

  // Makes the purchase if paying with crypto
  const purchase = async () => {
    const { id, name, legionId, price } = dropData.data;
    const subtotal = numCubes * parseFloat(price);
    const metadata = {
      email: user.email,
      username: user.username,
      userId: user.id,
      calculatedAge: calculateAge(user.dob),
      accountCreationDate: new Date(user.termsAcceptanceDate),
      reservationId: searchParams.get('reservationId'),
      correlationId: searchParams.get('correlationId'),
      dropId: id,
      dropName: name,
      legionId,
      unitPrice: price,
      quantity: numCubes,
      subtotal,
      fee: 0,
      total: subtotal + fee,
      currency: searchParams.get('purchaseCurrency1')
    };
    try {
      const updateData = isCreditCardPurchase
        ? {
            purchaseQuantity: numCubes // for Sardine credit card purchases, passing currency will return an error
          }
        : {
            purchaseQuantity: numCubes,
            purchaseCurrency1: searchParams.get('purchaseCurrency1')
          };
      const data = await makePurchase.mutateAsync({
        recaptchaToken: recaptchaToken,
        correlationId: searchParams.get('correlationId'),
        reservationId: searchParams.get('reservationId'),
        updateData: updateData
      });
      if (data) {
        window?.heap?.track('Purchase - Success', metadata);
        navigate({ pathname: '/success', search: `?numCubes=${numCubes}` });
      }
    } catch (error) {
      let errorMessage = error.message;
      window?.heap?.track('Purchase - Fail', {
        ...metadata,
        errorMessage
      });
      if (errorMessage === genericErrorMessage) {
        errorMessage = paymentErrorMessage;
      }
      if (isCreditCardPurchase) {
        // show fulfillment error page
        searchParams.set('error', encodeURIComponent(errorMessage));
        navigate({
          pathname: '/purchase-error',
          search: '?' + searchParams
        });
      } else {
        if (error.code === RECAPTCHA_ERROR_CODE) {
          navigate({
            pathname: '/verification-error',
            search: '?' + searchParams
          });
        } else {
          // redirect to crypto selection page
          searchParams.set('error', encodeURIComponent(errorMessage));
          navigate({
            pathname: '/select-currency',
            search: '?' + searchParams
          });
        }
      }
    }
  };

  const getIframeUrl = async () => {
    try {
      const { data } = await makeDeposit.mutateAsync({
        recaptchaToken: recaptchaToken,
        reservationId: searchParams.get('reservationId'),
        correlationId: searchParams.get('correlationId'),
        updateData: {
          sourceAmount: totalPriceWithoutFee + fee
        }
      });
      if (data) {
        searchParams.set('depositId', data.depositId);
        setSearchParams(searchParams.toString(), { replace: true });
        setClientToken(data.clientToken);
      }
    } catch (error) {
      if (error.code === RECAPTCHA_ERROR_CODE) {
        navigate({
          pathname: '/verification-error',
          search: '?' + searchParams
        });
      } else {
        navigate({
          pathname: '/error',
          search: '?dropId=' + searchParams.get('dropId')
        });
      }
    }
  };

  if (
    isOrderStatusInitialLoading ||
    (orderStatusData && orderStatusData.data.status === 'processed') // logic is happening to call purchase endpoint and redirect
  ) {
    return <Loading />;
  }

  return (
    <>
      <div className="container">
        <Header isPurchaseInProgress={!!clientToken || !!authResult} />
        <main className="mainContainer">
          <form
            id="complete-transaction-form"
            data-testid="complete-transaction-form"
            className={clientToken ? styles.noClickForm : ''}
          >
            <PaymentSteps />
            <h1 className="heading2">Review Your Order Summary</h1>

            <div
              id="transaction-summary-section"
              data-testid="transaction-summary-section"
              className={`${styles.orderSummary} borderMask`}
            >
              <div className={styles.purchaseSummary}>
                <div className={styles.summaryGroup}>
                  <p className={`bodyMicroReg ${styles.purchaseSummaryLabel}`}>
                    QUANTITY
                  </p>
                  <p
                    data-testid="purchase-quantity"
                    className={`bodySmBold ${styles.tranferredAmt}`}
                  >
                    {numCubes}
                  </p>
                </div>
                <div className={styles.summaryGroup}>
                  <p className={`bodyMicroReg ${styles.purchaseSummaryLabel}`}>
                    TOTAL
                  </p>
                  <p
                    data-testid="purchase-subtotal"
                    className={`bodySmBold ${styles.tranferredAmt}`}
                  >
                    {formatCurrencyUSD(totalPriceWithoutFee)}
                  </p>
                </div>

                {isCreditCardPurchase && (
                  <>
                    <p
                      className={`bodySmBold ${styles.purchaseSummaryLabel} ${styles.fee}`}
                    >
                      Fees
                    </p>
                    <div className={styles.summaryGroup}>
                      <p
                        className={`bodyMicroReg ${styles.purchaseSummaryLabel}`}
                      >
                        PROCESSING FEE
                      </p>
                      <p className={`bodyMicroReg ${styles.processingFee}`}>
                        5%&nbsp;
                      </p>
                      <p
                        data-testid="purchase-fee"
                        className={`bodyMicroReg ${styles.processingFeeTotal}`}
                      >
                        ({formatCurrencyUSD(fee)})
                      </p>
                    </div>
                  </>
                )}
                <p
                  className={`${styles.purchaseTotalDue} heading4 ${styles.purchaseSummaryLabel}`}
                >
                  Total Amount Due
                </p>
                <p
                  data-testid="purchase-total"
                  className={`order-total display4 ${styles.purchaseTotal}`}
                >
                  {formatCurrencyUSD(
                    isCreditCardPurchase
                      ? totalPriceWithoutFee + fee
                      : totalPriceWithoutFee
                  )}
                </p>
              </div>
            </div>

            <div className={styles.buttonCTA}>
              <div className={styles.buttonCont}>
                <Button
                  id="backButton"
                  data-testid="confirm-purchase-back-button"
                  children="Back"
                  // size="large"
                  variant="secondary"
                  fullWidth="true"
                  onPress={() => {
                    if (isCreditCardPurchase) {
                      navigate({
                        pathname: '/',
                        search: '?' + searchParams
                      });
                    } else {
                      navigate({
                        pathname: '/select-currency',
                        search: '?' + searchParams
                      });
                    }
                  }}
                />
              </div>
              <div className={styles.buttonCont}>
                <Button
                  id="completePurchaseButton"
                  data-testid="complete-purchase-button"
                  children="Complete Purchase"
                  // size="large"
                  variant="primary"
                  fullWidth="true"
                  isDisabled={makeDeposit.isLoading || makePurchase.isLoading}
                  isLoading={makeDeposit.isLoading || makePurchase.isLoading}
                  onPress={() => {
                    isCreditCardPurchase ? getIframeUrl() : purchase();
                  }}
                />
              </div>
            </div>
            {isCreditCardPurchase ? null : <TermsConditions />}
          </form>
        </main>
        <Footer />
        {clientToken && (
          <div className={styles.iframeModal}>
            <iframe
              ref={iframeRef}
              className={styles.paymentFrame}
              title="Payment Frame"
              src={
                process.env.REACT_APP_SARDINE_URL +
                clientToken +
                '&show_features=true'
              }
              frameBorder="0"
              height="100%"
              width="100%"
              sandbox="allow-storage-access-by-user-activation
      allow-scripts
      allow-same-origin
      allow-forms"
            ></iframe>
            {!authResult && (
              <>
                <div className={styles.desktopCancelContainer}>
                  <button
                    className={styles.desktopCancelButton}
                    onClick={() => {
                      resetDeposit();
                    }}
                  >
                    X
                  </button>
                </div>
                <div className={styles.mobileCancelContainer}>
                  <Button
                    size={'medium'}
                    fullWidth={true}
                    onPress={() => {
                      resetDeposit();
                    }}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
export default ConfirmOrder;
